import { useMutation, useQueryClient } from '@tanstack/react-query'

import { applyRichMenuToAllChannels } from 'api/richMenu'
import { useOrganizationScopeOrNull } from 'components/OrganizationScope/context'
import { getQueryOrganizationId } from 'configs/queryClient'
import { queryKeys } from 'constants/queryKeys'
import { lineStrings } from 'constants/strings'
import { useAuth } from 'contexts/AuthContext'
import { toErrorMessage } from 'hooks/utils'
import type { TCreateRichMenuRequest, TRichMenuApplyAllResult } from 'types'
import { toRichMenuOperationConflict } from './richMenuOperationConflict'

export function useApplyRichMenuToAllChannels() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  // 画像アップロードの後に呼ばれるため、開始時の組織へ適用する
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null
  const mutation = useMutation({
    mutationFn: async (
      body: TCreateRichMenuRequest,
    ): Promise<TRichMenuApplyAllResult> => {
      const response = await applyRichMenuToAllChannels(body, organizationId)
      return {
        ...response,
        deferred: response.deferred ?? [],
      }
    },
    onSettled: (_response, error) => {
      if (!user?.uid || toRichMenuOperationConflict(error)) return
      // アップロード後に呼ばれ onMutate 時点で移った先の組織になりうるため、開始時の組織と比べる
      if (organizationId !== getQueryOrganizationId()) return

      void queryClient.invalidateQueries({
        queryKey: [queryKeys.RICH_MENU_CURRENT, user.uid],
        exact: true,
      })
    },
  })

  const conflict = toRichMenuOperationConflict(mutation.error)

  return {
    applyToAllChannels: mutation.mutateAsync,
    isSubmitting: mutation.isPending,
    conflict,
    resetError: mutation.reset,
    error:
      mutation.error && !conflict
        ? toErrorMessage(
            mutation.error,
            lineStrings.management.richMenu.create.errors.applyAllFailed,
          )
        : undefined,
  }
}
